import React, { Suspense, useState } from 'react';

import ActionBar from '../../account/ActionBar/ActionBar';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import styled from '@emotion/styled';

import theme from '@vromo/ui/components/theme';

import { Route } from 'react-router';
import { Link } from 'react-router-dom';
import { css } from 'emotion';
import { transparentize } from 'polished';
import Tooltip from '@vromo/ui/components/Tooltip';
import Permissions from '@vromo/ui/components/Permissions';
import { slideInLeft } from '@vromo/ui/styles/animated';
import { UpgradeWrapper } from '../../freemium/UpgradeModal';
import { observer } from 'mobx-react-lite';

const SidebarList = styled.ul`
    width: 100%;
    padding: 0;
    list-style-type: none;
    text-align: left;
    margin: auto;

    li {
        border-right: 3px solid transparent;
        transition: background-color 0.2s ease-in-out;

        a {
            display: inline-block;
            padding: 16px;

            i {
                transition: color 0.2s ease-in-out;
            }
        }

        &:hover {
            background-color: ${transparentize(1 - 0.18, theme.primary)};

            a {
                display: inline-block;
                padding: 16px;
                color: ${theme.primary};

                i {
                    color: ${theme.primary};
                }
            }

            @media (max-width: 1000px) {
                background-color: ${transparentize(1 - 0.18, '#fff')};

                a {
                    color: #fff;

                    i {
                        color: #fff;
                    }
                }
            }
        }
    }
`;

function SideBarLink({
    icon,
    path,
    label,
    isFreemium
}: {
    icon: string;
    path: string;
    label: string;
    isFreemium?: boolean;
}) {
    const renderLink = (icon: string, path: string, label: string) => (
        <Link
            to={path}
            data-sidebar-link={label}
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
            }}>
            <FontAwesome weight="regular" icon={icon} x2 fixed />

            <span
                className={css`
                    display: none;
                    font-size: 16px;
                    margin-left: 8px;
                    @media (max-width: 1000px) {
                        display: inline-block;
                    }
                `}>
                {label}
            </span>
        </Link>
    );

    return (
        <Route
            path={path}
            exact
            children={({ match }) => {
                return (
                    <Tooltip position="right" label={label}>
                        <li
                            className={css`
                                border-right: ${match
                                    ? `3px solid ${theme.primary} !important`
                                    : 'none'};
                                background-color: ${match
                                    ? transparentize(1 - 0.18, theme.primary)
                                    : 'transparent'};

                                a {
                                    color: ${match
                                        ? theme.primary
                                        : transparentize(0.5, theme.primary)};
                                }

                                @media (max-width: 1000px) {
                                    border-right: ${match ? `3px solid white !important` : 'none'};
                                    background-color: ${match
                                        ? transparentize(1 - 0.18, '#fff')
                                        : 'transparent'};

                                    a {
                                        color: ${match ? '#fff' : transparentize(0.4, '#fff')};
                                    }
                                }
                            `}>
                            {isFreemium ? (
                                <UpgradeWrapper>{renderLink(icon, path, label)}</UpgradeWrapper>
                            ) : (
                                renderLink(icon, path, label)
                            )}
                        </li>
                    </Tooltip>
                );
            }}
        />
    );
}

export const SpatulaActionBarLayout = observer(function SpatulaActionBarLayout({ children }) {
    const [open, setOpen] = useState(false);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'stretch',
                height: '100vh'
            }}>
            <div
                onClick={() => setOpen(false)}
                className={css`
                    display: none;
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background-color: rgba(0, 0, 0, 0.3);
                    z-index: 100000;

                    @media (max-width: 1000px) {
                        display: ${open ? 'block' : 'none'};
                    }
                `}
            />

            <ActionBar toggleSidebar={() => setOpen(!open)} />

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flex: 1,
                    backgroundColor: '#f3f3f3',
                    minHeight: 0
                }}>
                <div
                    className={css`
                        display: flex;
                        flex-direction: column;
                        background-color: white;
                        width: 70px;

                        @media (max-width: 1000px) {
                            width: 170px;
                            display: ${open ? 'flex' : 'none'};
                            animation: ${slideInLeft} 0.2s ease-in;
                            position: fixed;
                            top: 0;
                            bottom: 0;
                            left: 0;
                            z-index: 200000;
                            background-color: ${theme.primary};
                        }
                    `}>
                    <div
                        onClick={() => setOpen(false)}
                        className={css`
                            display: none;
                            font-size: 36px;
                            margin-right: 8px;
                            text-align: right;
                            cursor: pointer;
                            color: white;
                            @media (max-width: 1000px) {
                                display: block;
                            }
                        `}>
                        &times;
                    </div>
                    <div
                        className={css`
                            align-self: stretch;
                            display: flex;
                            align-items: center;
                            flex: 1;
                            min-height: 0;
                            overflow-x: hidden;
                            overflow-y: auto;
                        `}>
                        <SidebarList>
                            <Permissions minPosition="client">
                                <SideBarLink label="Dashboard" icon="th-large" path="/" />
                            </Permissions>

                            <Permissions minPosition="client">
                                <SideBarLink
                                    label="Dispatch"
                                    icon="map-marked-alt"
                                    path="/dispatch"
                                />
                            </Permissions>
                            <Permissions minPosition="user">
                                <SideBarLink label="Reports" icon="analytics" path="/report" />
                            </Permissions>
                            {/* {isDeliverThat && (
                                <SideBarLink label="Plan" icon="ruler-combined" path="/plan" />
                            )} */}

                            <Permissions minPosition="freemiumAdmin">
                                <>
                                    <SideBarLink label="Plan" icon="ruler-combined" path="/plan" />
                                    <SideBarLink
                                        label="Auto Dispatch"
                                        icon="project-diagram"
                                        path="/policies"
                                        />
                                    <SideBarLink label="Teams" icon="users" path="/teams" />
                                    <SideBarLink
                                        label="Campaigns"
                                        icon="bullhorn"
                                        path="/campaigns"
                                        isFreemium
                                        />
                                    <SideBarLink label="Sites" icon="building" path="/sites" />
                                </>
                            </Permissions>
                        </SidebarList>
                    </div>
                </div>

                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}>
                    <Suspense fallback={<div />}>{children}</Suspense>
                </div>
            </div>
        </div>
    );
});
